import type { AuditEvent } from "@/types/ui/audit"
import type { AuditFieldConfig } from "@/lib/audit/audit-config"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Separator } from "@/components/ui/separator"
import { ChevronDown, ChevronRight, Copy } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { useFormatters } from "@/hooks/use-formatters"

interface ChangeHistoryTableProps {
  events: AuditEvent[]
  currentVersion: number
  config: AuditFieldConfig
}

export function ChangeHistoryTable({ events, currentVersion, config }: ChangeHistoryTableProps) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-card">
        <div className="rounded-full bg-muted p-3 mb-4">
          <Copy className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-1">No changes found</h3>
        <p className="text-sm text-muted-foreground">Try adjusting your filters or search query</p>
      </div>
    )
  }

  const sortedEvents = [...events].sort((a, b) => {
    // Sort by version descending
    if (a.version !== b.version) {
      return b.version - a.version
    }
    // Within same version, show active versions before deleted ones
    if (a.isDeleted !== b.isDeleted) {
      return a.isDeleted ? 1 : -1
    }
    return 0
  })

  return (
    <div className="space-y-4">
      {sortedEvents.map((event) => (
        <VersionCard key={event.id} event={event} currentVersion={currentVersion} config={config} />
      ))}
    </div>
  )
}

function VersionCard({ event, currentVersion, config }: { event: AuditEvent; currentVersion: number; config: AuditFieldConfig }) {
  const [isOpen, setIsOpen] = useState(event.version === currentVersion && !event.isDeleted)
  const { formatDate } = useFormatters()
  const { formatStatus } = config

  const effectiveDate = formatDate(event.modifiedDateTime)

  return (
    <Collapsible 
      open={isOpen} 
      onOpenChange={setIsOpen} 
      className={`border rounded-lg bg-card shadow-sm ${event.isDeleted ? 'opacity-60' : ''}`}
    >
      {/* Version Header */}
      <div className="px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
        <div className="flex items-center gap-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="p-0 h-auto hover:bg-transparent">
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </Button>
          </CollapsibleTrigger>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-base font-semibold">Version: {event.version}</span>
              <Badge variant="secondary" className="font-normal">
                Status: {formatStatus(event.statusAfter || event.status)}
              </Badge>
            </div>
            {event.workflowInstanceId && (
              <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                <span>Workflow ID: {event.workflowInstanceId}</span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(event.workflowInstanceId!)
                  }}
                >
                  Copy
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">Effective:</span> <span className="font-medium">{effectiveDate}</span>
        </div>
      </div>

      <CollapsibleContent>
        <div className="px-6 pb-6">
          <StatusChangesSections event={event} config={config} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function StatusChangesSections({
  event,
  config,
}: {
  event: AuditEvent
  config: AuditFieldConfig
}) {
  const { formatStatus, formatAction } = config
  const { formatTimestamp } = useFormatters()

  // Show each audit record as a collapsible (newest first)
  const auditsToShow = [...event.audits].reverse()
  
  const [openAudits, setOpenAudits] = useState<Record<string, boolean>>(() => {
    // Initialize with latest audit open
    const initial: Record<string, boolean> = {}
    const latestAudit = event.audits.find(a => a.isLatest)
    if (latestAudit) {
      initial[latestAudit.id] = true
    }
    return initial
  })

  return (
    <div className="space-y-3">
      {auditsToShow.map((audit, idx) => {
        const isLatest = audit.isLatest
        const hasSectionChanges = audit.sectionGroups.length > 0
        const hasContent = hasSectionChanges || !!audit.comment
        const isOpen = openAudits[audit.id] ?? false

        const headerContent = (
          <div className={
            isLatest 
              ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md py-3 px-4"
              : "border rounded-md py-3 px-4 " + (hasContent ? "hover:bg-muted/30 transition-colors" : "")
          }>
            <div className="grid grid-cols-[200px_1fr_200px] gap-4 items-center">
              <div className="flex items-center gap-3">
                {hasContent ? (
                  isOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )
                ) : (
                  <div className="w-4" /> 
                )}
                <span className={isLatest ? "font-semibold text-sm" : "text-sm text-muted-foreground"}>
                  {formatStatus(audit.status)}
                </span>
              </div>
              <div className="text-sm text-muted-foreground text-center">
                <span className="font-medium">{formatAction(audit.action)}</span> by {audit.modifiedBy.displayName}
              </div>
              <div className="text-sm text-muted-foreground text-right">
                {formatTimestamp(audit.modifiedDateTime)}
              </div>
            </div>
          </div>
        )

        return (
          <div key={audit.id}>
            {hasContent ? (
              <Collapsible 
                open={isOpen}
                onOpenChange={(open) => setOpenAudits(prev => ({ ...prev, [audit.id]: open }))}
              >
                <CollapsibleTrigger className="w-full">
                  {headerContent}
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="mt-3 pl-4">
                    {audit.comment && (
                      <div className={hasSectionChanges ? "mb-4 pb-4" : "mb-4"}>
                        <div className="text-sm">
                          <span className="font-medium text-muted-foreground">Comment:</span>{" "}
                          <span className="text-foreground">{audit.comment}</span>
                        </div>
                        {hasSectionChanges && (
                          <Separator className="mt-4" />
                        )}
                      </div>
                    )}
                    {audit.sectionGroups.map((sectionGroup) => (
                      <div
                        key={sectionGroup.section}
                        className="mb-4 last:mb-0 border-2 border-blue-200 dark:border-blue-800 rounded-md p-4 bg-card"
                      >
                        <h4 className="font-semibold text-base mb-3">{sectionGroup.section}</h4>
                        <div className="space-y-4">
                          {sectionGroup.changeGroups.map((changeGroup, groupIdx) => (
                            <div key={groupIdx} className="space-y-2">
                              {changeGroup.parentLabel && (
                                <h5 className="font-semibold text-sm text-muted-foreground pl-2">
                                  {changeGroup.parentLabel}
                                </h5>
                              )}
                              <table className="w-full text-sm table-fixed">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-[30%]">
                                      FIELD NAME
                                    </th>
                                    <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-[35%]">
                                      OLD
                                    </th>
                                    <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-[35%]">
                                      NEW
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {changeGroup.changes.map((change, changeIdx) => (
                                    <tr key={changeIdx} className="border-b last:border-0">
                                      <td className="py-3 px-2 font-medium w-[30%]">
                                        {change.fieldName}
                                      </td>
                                      <td className="py-3 px-2 text-muted-foreground w-[35%] align-top">
                                        {change.formattedOldValue || <span className="text-muted-foreground/60">-</span>}
                                      </td>
                                      <td className="py-3 px-2 font-medium w-[35%] align-top">
                                        {change.formattedNewValue || <span className="text-muted-foreground/60">-</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              headerContent
            )}
          </div>
        )
      })}
    </div>
  )
}

